import { create } from 'zustand';
import {
  BlobItem,
  BreadcrumbItem,
  UploadProgress,
  AuthState,
  EmailMetadata,
  ActivityLogEntry,
  ActivityType,
  TreeRow,
} from '@/types';
import {
  flattenTree,
  flattenTreeFrom,
  flattenFiltered,
  parentFolderPath,
  ancestorsOf,
} from '@/utils/tree';
import { sharePointService } from '@/services/sharePointService';
import { authService, emailService } from '@/services';
import { config, getItemDateDisplay, isEmailFile } from '@/config';
import { logger } from '@/utils/logger';
import { trackEvent } from '@/services/telemetry';
import { getPreviewKind } from '@/utils/fileTypes';
import { mapWithConcurrency } from '@/utils/concurrency';
import { detectMobile } from '@/utils/platform';
import { saveMessages } from '@/services/saveService';
import {
  FolderFavorite,
  loadFolderFavorites,
  saveFolderFavorites,
} from '@/services/favoritesStorage';

export interface SiteInfo {
  id: string;
  name: string;
  accessible: boolean;
}

interface FileBrowserStore {
  // Auth state
  auth: AuthState;

  // Multi-site: the sites the user can browse, and the currently selected one
  // (null = show the home/site-picker view).
  sites: SiteInfo[];
  // False until the first access-probe completes (so the picker shows a loader
  // rather than flashing sites the user cannot open).
  sitesLoaded: boolean;
  authStale: boolean;
  currentSiteId: string | null;

  // File browser state
  currentPath: string;
  items: BlobItem[];
  selectedItems: Set<string>;
  isLoading: boolean;
  error: string | null;
  // Transient action feedback rendered as a Fluent MessageBar (success
  // auto-dismisses; errors stay until dismissed).
  notification: { type: 'success' | 'error'; text: string } | null;
  sortBy: 'name' | 'date' | 'size';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  // True when `items` are recursive Graph search results rather than a folder listing.
  isSearching: boolean;
  // During search, how many leading `items` are current-folder name matches
  // (the "In this folder" group); the remainder are the "More results" group.
  searchLocalCount: number;

  // Folder favorites are persisted as a site/path tuple. The view is transient:
  // switching sites always returns to the normal tree.
  browserView: 'all' | 'favorites';
  folderFavorites: FolderFavorite[];
  favoriteAvailability: Record<string, 'available' | 'unavailable'>;

  // Preview state
  previewItem: BlobItem | null;
  previewMetadata: EmailMetadata | null;
  previewContent: string | null;
  // Sanitable HTML body for email previews (rendered in a sandboxed iframe).
  previewHtml: string | null;
  isPreviewLoading: boolean;

  // Upload state
  uploads: UploadProgress[];
  isUploading: boolean;

  // Drag state
  isDragging: boolean;
  dragOverPath: string | null;

  // Mobile email save mode. On iOS/Android there is no drag-drop, so the
  // read-surface "Save to FileSpace" button opens the pane with saveEmail set;
  // the user browses to a folder and taps Save here. Desktop never sets this.
  isMobile: boolean;
  saveEmail: { itemId: string; subject: string; date?: Date } | null;
  isSavingEmail: boolean;

  // Folder dialog state
  isCreateFolderOpen: boolean;
  isCreatingFolder: boolean;

  // Activity log
  activityLog: ActivityLogEntry[];

  // Tree browsing
  expandedPaths: Set<string>;
  treeLoading: boolean;
  // Folders whose children are currently being fetched (shows a spinner on the
  // expanding folder row).
  loadingPaths: Set<string>;
  // The flattened, currently-visible tree rows. Stable reference; rebuilt only
  // on tree changes via rebuildVisibleRows.
  visibleRows: TreeRow[];
  rebuildVisibleRows: () => void;
  getVisibleRows: () => TreeRow[];
  getDisplayedItems: () => BlobItem[];
  loadTree: () => Promise<void>;
  reloadFolder: (path: string) => Promise<void>;
  toggleExpand: (path: string) => Promise<void>;
  collapseAll: () => void;

  // Computed (as functions)
  getBreadcrumbs: () => BreadcrumbItem[];
  getFilteredItems: () => BlobItem[];
  getSelectedItems: () => BlobItem[];

  // Auth actions
  initAuth: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;

  // Multi-site actions
  loadSites: (forceTokenRefresh?: boolean) => Promise<void>;
  selectSite: (id: string, initialPath?: string) => void;
  goHome: () => void;

  // Navigation actions
  navigate: (path: string) => void;
  navigateUp: () => void;
  refresh: () => Promise<void>;
  // Background re-list of the current folder (no spinner); updates items only if
  // they actually changed. Used by auto-refresh (focus/visibility + poll).
  silentRefresh: () => Promise<void>;

  // Selection actions
  // Replace the whole selection (DetailsList Selection object sync).
  setSelection: (paths: string[]) => void;
  selectAll: () => void;
  clearSelection: () => void;

  // View actions
  setSortBy: (sortBy: 'name' | 'date' | 'size') => void;
  toggleSortOrder: () => void;
  setSearchQuery: (query: string) => void;
  runSearch: (query: string) => Promise<void>;
  setBrowserView: (view: 'all' | 'favorites') => void;
  hydrateFolderFavorites: () => void;
  getSiteFavorites: () => FolderFavorite[];
  isFolderFavorite: (path: string) => boolean;
  addFolderFavorite: (item: BlobItem) => Promise<void>;
  removeFolderFavorite: (path: string) => Promise<void>;
  openFolderFavorite: (favorite: FolderFavorite) => Promise<boolean>;
  setError: (error: string | null) => void;
  notify: (type: 'success' | 'error', text: string) => void;
  dismissNotification: () => void;

  // File operations
  createFolder: (name: string) => Promise<void>;
  deleteItem: (item: BlobItem) => Promise<void>;
  deleteSelected: () => Promise<void>;
  downloadSelected: () => Promise<void>;
  downloadItem: (item: BlobItem) => Promise<void>;
  openFile: (item: BlobItem) => Promise<void>;
  copyLink: (item: BlobItem) => Promise<void>;
  attachSelectedToEmail: () => Promise<void>;
  attachItemToEmail: (item: BlobItem) => Promise<void>;

  // Upload actions
  uploadFiles: (files: File[], targetPath?: string) => Promise<void>;
  clearCompletedUploads: () => void;
  // Cancel in-progress uploads (#5).
  cancelUpload: (uploadId: string) => void;
  cancelAllUploads: () => void;

  // Preview actions
  setPreviewItem: (item: BlobItem | null) => void;
  loadPreview: (item: BlobItem) => Promise<void>;

  // Drag actions
  setIsDragging: (isDragging: boolean) => void;
  setDragOverPath: (path: string | null) => void;
  handleDrop: (targetPath: string) => Promise<void>;

  // Mobile email save mode
  initSaveMode: () => void;
  saveCurrentEmailHere: () => Promise<void>;
  cancelSaveMode: () => void;

  // Dialog actions
  setCreateFolderOpen: (open: boolean) => void;
  isDeleteConfirmOpen: boolean;
  setDeleteConfirmOpen: (open: boolean) => void;
  // Move dialog: the items being moved, or null when closed.
  moveDialogItems: BlobItem[] | null;
  openMoveDialog: (items: BlobItem[]) => void;
  closeMoveDialog: () => void;
  // Rename dialog: the item being renamed, or null when closed.
  renameDialogItem: BlobItem | null;
  openRenameDialog: (item: BlobItem) => void;
  closeRenameDialog: () => void;
  renameItem: (item: BlobItem, newName: string) => Promise<void>;
  moveItems: (items: BlobItem[], destFolder: string) => Promise<void>;

  // Activity log actions
  addActivity: (type: ActivityType, message: string, details?: string, path?: string) => void;
  clearActivityLog: () => void;
}

const sortItems = (items: BlobItem[], sortBy: string, sortOrder: string): BlobItem[] => {
  const desc = sortOrder === 'desc';
  return [...items].sort((a, b) => {
    // Match Explorer: folders are grouped together, on TOP for ascending and on
    // the BOTTOM for descending (so files sit above folders when sorting desc).
    if (a.isFolder !== b.isFolder) {
      const folderFirst = a.isFolder ? -1 : 1;
      return desc ? -folderFirst : folderFirst;
    }

    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'date':
        comparison =
          (getItemDateDisplay(a)?.date.getTime() || 0) -
          (getItemDateDisplay(b)?.date.getTime() || 0);
        break;
      case 'size':
        comparison = (a.size || 0) - (b.size || 0);
        break;
    }

    return desc ? -comparison : comparison;
  });
};

// Storage keys for persisting the open path + selected site
const PATH_STORAGE_KEY = 'filespace_current_path';
const SITE_STORAGE_KEY = 'filespace_current_site';

const readLocal = (key: string): string => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeLocal = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage not available
  }
};

const getPersistedPath = (): string => readLocal(PATH_STORAGE_KEY);
const persistPath = (path: string): void => writeLocal(PATH_STORAGE_KEY, path);

// Folder-listing cache (#35), keyed by site|path. Raw (unsorted) listings;
// sorting happens on read so sort changes do not invalidate the cache.
// Stale-while-revalidate: cached folders render instantly, then refresh()
// fetches fresh data in the background. Mutations invalidate their path.
const listingCache = new Map<string, BlobItem[]>();
const listingKey = (siteId: string | null, path: string): string =>
  `${siteId || 'default'}|${path}`;

// Per-folder children for the tree, keyed by plain folder path (root is '').
// Rebuilt per site; the existing listingCache stays for search/single-folder use.
const treeChildren = new Map<string, BlobItem[]>();

// Re-sort every loaded folder's children in place, so changing the sort
// (e.g. clicking the Modified column) reorders the whole tree without refetching.
function resortTreeChildren(sortBy: string, sortOrder: string): void {
  for (const [path, children] of treeChildren) {
    treeChildren.set(path, sortItems(children, sortBy, sortOrder));
  }
}

// In-place search state (module-level, like treeChildren). searchReveal is the
// set of paths the filtered tree may show (matches + the folder paths leading to
// them, plus children of folders the user manually expands while searching).
// preSearchExpanded snapshots the user's expansion so it is restored on clear.
let searchReveal = new Set<string>();
let preSearchExpanded: Set<string> | null = null;

// Telemetry gate: only emit `sites.accessRecovered` after we actually surfaced a
// `sites.noAccess`. Without this, the first probe on every boot "recovers" from
// the default `accessible: false` state and floods a meaningless signal.
let sawNoAccess = false;

// Name matches across everything currently loaded in the tree.
function loadedTreeNameMatches(ql: string): string[] {
  const out: string[] = [];
  for (const children of treeChildren.values()) {
    for (const it of children) {
      if (it.name.toLowerCase().includes(ql)) out.push(it.path);
    }
  }
  return out;
}

// Build the reveal/expand sets from the match paths and rebuild the rows.
// A folder whose NAME matches is shown COLLAPSED (its files are subsumed -
// expand to see them all), so a folder hit does not look half-empty. A file
// match reveals and expands the folders on the path down to it.
// (Folder paths carry a trailing slash; file paths do not.)
function applySearchReveal(set: StoreSet, get: StoreGet, matchPaths: string[]): void {
  const folderMatches = matchPaths.filter((p) => p.endsWith('/'));
  // Drop file matches that live inside a matched folder; the folder match
  // covers them and stays collapsed.
  const fileMatches = matchPaths.filter(
    (p) => !p.endsWith('/') && !folderMatches.some((f) => p.startsWith(f)),
  );

  const reveal = new Set<string>();
  const expand = new Set<string>();
  // Matched folders: show them and the path to them, but do NOT expand the
  // matched folder itself.
  for (const f of folderMatches) {
    reveal.add(f);
    for (const a of ancestorsOf(f)) {
      reveal.add(a);
      expand.add(a);
    }
  }
  // Matched files: reveal and expand the folders leading to them.
  for (const m of fileMatches) {
    reveal.add(m);
    for (const a of ancestorsOf(m)) {
      reveal.add(a);
      expand.add(a);
    }
  }

  // Honor the pre-search view: any revealed folder that was already expanded in
  // the default view stays expanded with ALL its children, so folders you were
  // working in keep showing their contents during search (fixpoint to cover
  // nested pre-expanded folders).
  if (preSearchExpanded) {
    let changed = true;
    while (changed) {
      changed = false;
      for (const p of [...reveal]) {
        if (p.endsWith('/') && preSearchExpanded.has(p) && !expand.has(p)) {
          expand.add(p);
          changed = true;
          for (const c of treeChildren.get(p) || []) {
            if (!reveal.has(c.path)) reveal.add(c.path);
          }
        }
      }
    }
  }

  searchReveal = reveal;
  set({ isSearching: true, expandedPaths: expand });
  get().rebuildVisibleRows();
}

const EXPANDED_KEY_PREFIX = 'filespace_expanded_';
const FAVORITES_EXPANDED_KEY_PREFIX = 'filespace_expanded_favorites_';
type BrowserView = 'all' | 'favorites';
const expandedStorageKey = (view: BrowserView, siteId: string | null): string =>
  view === 'favorites'
    ? `${FAVORITES_EXPANDED_KEY_PREFIX}${siteId || 'default'}`
    : `${EXPANDED_KEY_PREFIX}${siteId || 'default'}`;
const readExpanded = (view: BrowserView, siteId: string | null): Set<string> => {
  try {
    const raw = localStorage.getItem(expandedStorageKey(view, siteId));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};
const persistExpanded = (view: BrowserView, siteId: string | null, paths: Set<string>): void => {
  try {
    localStorage.setItem(expandedStorageKey(view, siteId), JSON.stringify([...paths]));
  } catch {
    return;
  }
};

interface ViewSnapshot {
  expandedPaths: Set<string>;
  sortBy: 'name' | 'date' | 'size';
  sortOrder: 'asc' | 'desc';
}

const viewStash: Partial<Record<BrowserView, ViewSnapshot>> = {};

function stashCurrentView(get: StoreGet): void {
  const state = get();
  viewStash[state.browserView] = {
    expandedPaths: new Set(state.expandedPaths),
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
  };
}

function takeViewSnapshot(view: BrowserView, siteId: string | null): ViewSnapshot {
  const stashed = viewStash[view];
  if (stashed) {
    delete viewStash[view];
    return stashed;
  }
  return { expandedPaths: readExpanded(view, siteId), sortBy: 'name', sortOrder: 'asc' };
}

function purgeStashedExpandedPath(path: string): void {
  for (const snapshot of Object.values(viewStash)) {
    snapshot?.expandedPaths.delete(path);
  }
}

const favoriteKey = (siteId: string, path: string): string => `${siteId}|${path}`;

const sessionOnlyFavoriteHydrationCache = new Map<string, BlobItem>();
const favoriteHydrationInFlight = new Set<string>();

async function hydrateFavoriteRoots(get: StoreGet): Promise<void> {
  const siteId = get().currentSiteId;
  if (!siteId) return;
  const pending = get().folderFavorites.filter((f) => {
    if (f.siteId !== siteId) return false;
    const key = favoriteKey(f.siteId, f.path);
    return !sessionOnlyFavoriteHydrationCache.has(key) && !favoriteHydrationInFlight.has(key);
  });
  if (pending.length === 0) return;

  for (const f of pending) favoriteHydrationInFlight.add(favoriteKey(f.siteId, f.path));

  const byParent = new Map<string, FolderFavorite[]>();
  for (const f of pending) {
    const parent = parentFolderPath(f.path);
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  }

  await Promise.all(
    [...byParent.entries()].map(async ([parent, favs]) => {
      try {
        const children = await sharePointService.listItems(parent);
        if (get().currentSiteId !== siteId) return;
        for (const f of favs) {
          const match = children.find((c) => c.path === f.path);
          if (match) sessionOnlyFavoriteHydrationCache.set(favoriteKey(f.siteId, f.path), match);
        }
      } catch (error) {
        logger.debug('Favorite hydration failed for parent:', parent, error);
      } finally {
        for (const f of favs) favoriteHydrationInFlight.delete(favoriteKey(f.siteId, f.path));
      }
    }),
  );

  if (get().currentSiteId === siteId && get().browserView === 'favorites') {
    get().rebuildVisibleRows();
  }
}

let favoritesWriteQueue: Promise<void> = Promise.resolve();

function queueFavoritesWrite(favorites: FolderFavorite[]): Promise<FolderFavorite[]> {
  const write = favoritesWriteQueue.then(() => saveFolderFavorites(favorites));
  favoritesWriteQueue = write.then(
    () => undefined,
    () => undefined,
  );
  return write;
}

// AbortControllers for in-flight uploads, keyed by upload id (#5 cancel).
const uploadAborters = new Map<string, AbortController>();
const EMAIL_HEADER_READ_LIMIT = 256 * 1024;

type StoreSet = (
  partial: Partial<FileBrowserStore> | ((state: FileBrowserStore) => Partial<FileBrowserStore>),
) => void;
type StoreGet = () => FileBrowserStore;

// Upload one file and track it in the uploads tray. Shared by the batch loop
// and by Save Anyway on a held duplicate.
async function performUpload(
  set: StoreSet,
  get: StoreGet,
  uploadId: string,
  file: File,
  destination: string,
  contentHash?: string,
  emailReceivedDate?: Date,
  hasAttachments?: boolean,
): Promise<void> {
  const aborter = new AbortController();
  uploadAborters.set(uploadId, aborter);
  try {
    set((state) => ({
      uploads: state.uploads.map((u) => (u.id === uploadId ? { ...u, status: 'uploading' } : u)),
    }));

    const displayName = await sharePointService.uploadFile(
      file,
      destination,
      file.name,
      contentHash,
      (progress) => {
        set((state) => ({
          uploads: state.uploads.map((u) => (u.id === uploadId ? { ...u, progress } : u)),
        }));
      },
      aborter.signal,
      emailReceivedDate,
      hasAttachments,
    );

    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.id === uploadId ? { ...u, status: 'complete', progress: 100, fileName: displayName } : u,
      ),
    }));

    get().addActivity('upload', `Uploaded "${displayName}"`, undefined, destination || '/');
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') {
      set((state) => ({
        uploads: state.uploads.map((u) => (u.id === uploadId ? { ...u, status: 'cancelled' } : u)),
      }));
      get().addActivity(
        'upload',
        `Cancelled upload of "${file.name}"`,
        undefined,
        destination || '/',
      );
      return;
    }
    const errorMessage = error instanceof Error ? error.message : 'Upload failed';
    // Always surface the full error in the browser console for diagnosis.
    logger.error(`Upload failed for "${file.name}":`, error);
    set((state) => ({
      uploads: state.uploads.map((u) =>
        u.id === uploadId ? { ...u, status: 'error', error: errorMessage } : u,
      ),
    }));

    get().addActivity('error', `Failed to upload "${file.name}"`, errorMessage, destination || '/');
  } finally {
    uploadAborters.delete(uploadId);
  }
}

// Download files from SharePoint and attach them to the message being
// composed. Shared by the toolbar (current selection) and the preview panel
// (the single previewed item); only the toolbar path clears the selection.
async function attachFilesToEmail(
  set: StoreSet,
  get: StoreGet,
  filesToAttach: BlobItem[],
  clearSelectionOnSuccess: boolean,
): Promise<void> {
  // Re-check compose mode with a small delay to ensure Office is ready
  await new Promise((resolve) => setTimeout(resolve, 100));

  if (!emailService.isComposeMode()) {
    get().notify('error', 'Cannot attach files: not composing an email.');
    return;
  }

  set({ isLoading: true, error: null });

  try {
    const attachments: Array<{ fileName: string; base64Content: string; contentType: string }> = [];

    for (const item of filesToAttach) {
      try {
        const blob = await sharePointService.getFileBlob(item.path);
        const arrayBuffer = await blob.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
        );

        // Determine content type - special handling for EML files
        let contentType = item.contentType || 'application/octet-stream';
        if (item.name.toLowerCase().endsWith('.eml')) {
          contentType = 'message/rfc822';
        }

        attachments.push({
          fileName: item.name,
          base64Content: base64,
          contentType,
        });
      } catch (downloadErr) {
        logger.error('Failed to download file for attachment:', item.name, downloadErr);
      }
    }

    if (attachments.length === 0) {
      get().notify('error', 'Failed to download files for attachment.');
      return;
    }

    const result = await emailService.attachFiles(attachments);

    if (result.success.length > 0) {
      get().addActivity('upload', `Attached ${result.success.length} file(s) to email`);
      get().notify('success', `Attached ${result.success.length} file(s) to email`);
      if (clearSelectionOnSuccess) {
        set({ selectedItems: new Set() });
      }
    }

    if (result.failed.length > 0) {
      const msg =
        result.success.length > 0
          ? `Attached ${result.success.length}, failed: ${result.failed.join(', ')}`
          : `Failed to attach: ${result.failed.join(', ')}`;
      get().notify('error', msg);
    }
  } catch (err) {
    logger.error('Attach error:', err);
    set({ error: 'Unable to attach files. Please try again.' });
  } finally {
    set({ isLoading: false });
  }
}

// Debounce timer for the recursive search box.
let searchTimer: ReturnType<typeof setTimeout> | null = null;

// Cheap signature of a listing, to detect whether a background refresh changed
// anything (avoids re-rendering / losing selection when nothing changed).
const listSignature = (items: BlobItem[]): string =>
  items.map((i) => `${i.path}|${i.size ?? ''}|${i.lastModified?.getTime() ?? ''}`).join('\n');

// Copy text to the clipboard from inside the Office add-in iframe. The async
// Clipboard API (navigator.clipboard) is blocked there by permissions policy,
// so use a temp textarea + execCommand('copy'), falling back to the async API.
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch {
    // fall through to the async API
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

export const useFileBrowserStore = create<FileBrowserStore>((set, get) => ({
  // Initial state - restore path from localStorage
  auth: { isAuthenticated: false, isLoading: true },
  // Start NOT accessible: the picker waits for the per-user access probe
  // (loadSites) before showing anything, so a site the user cannot open never
  // flashes into the list.
  sites: config.sharePoint.sites.map((s) => ({ ...s, accessible: false })),
  sitesLoaded: false,
  authStale: false,
  currentSiteId: null,
  currentPath: getPersistedPath(),
  items: [],
  selectedItems: new Set(),
  isLoading: false,
  error: null,
  notification: null,
  sortBy: 'name',
  sortOrder: 'asc',
  searchQuery: '',
  isSearching: false,
  searchLocalCount: 0,
  browserView: 'all',
  folderFavorites: loadFolderFavorites(),
  favoriteAvailability: {},
  previewItem: null,
  previewMetadata: null,
  previewContent: null,
  previewHtml: null,
  isPreviewLoading: false,
  uploads: [],
  isUploading: false,
  isDragging: false,
  dragOverPath: null,
  isMobile: false,
  saveEmail: null,
  isSavingEmail: false,
  isCreateFolderOpen: false,
  isCreatingFolder: false,
  activityLog: [],
  expandedPaths: new Set<string>(),
  treeLoading: false,
  loadingPaths: new Set<string>(),
  visibleRows: [],

  // Computed as functions
  getBreadcrumbs: () => {
    const { currentPath } = get();
    const crumbs: BreadcrumbItem[] = [{ name: 'Root', path: '' }];

    if (currentPath) {
      const parts = currentPath.split('/').filter(Boolean);
      parts.forEach((name, index) => {
        crumbs.push({
          name,
          path: parts.slice(0, index + 1).join('/') + '/',
        });
      });
    }

    return crumbs;
  },

  getFilteredItems: () => {
    // Search is server-side (recursive Graph search) now, so `items` already holds
    // either the folder listing or the search results.
    return get().items;
  },

  // visibleRows is held in state and rebuilt only when the tree actually changes
  // (expand/collapse, load, reload), NOT on every store update. This keeps a
  // stable array reference so FileList's row/column memos hold across unrelated
  // updates like dragOverPath changing rapidly during a drag.
  getVisibleRows: () => get().visibleRows,

  getSelectedItems: () => {
    const { visibleRows, selectedItems } = get();
    return visibleRows.filter((r) => selectedItems.has(r.item.path)).map((r) => r.item);
  },

  // Search now filters the tree in place, so the displayed items are always the
  // visible tree rows (pruned to matches while searching).
  getDisplayedItems: () => get().visibleRows.map((r) => r.item),

  rebuildVisibleRows: () => {
    const state = get();
    if (state.browserView === 'favorites') {
      const roots = state.folderFavorites
        .filter((f) => f.siteId === state.currentSiteId)
        .map((f) => {
          const hydrated = sessionOnlyFavoriteHydrationCache.get(favoriteKey(f.siteId, f.path));
          return {
            name: f.name,
            path: f.path,
            isFolder: f.isFolder,
            metadata: {},
            ...hydrated,
          } as BlobItem;
        });
      const sortedRoots = sortItems(roots, state.sortBy, state.sortOrder);
      set({ visibleRows: flattenTreeFrom(treeChildren, state.expandedPaths, sortedRoots) });
      return;
    }
    set({
      visibleRows: state.isSearching
        ? flattenFiltered(treeChildren, state.expandedPaths, searchReveal)
        : flattenTree(treeChildren, state.expandedPaths),
    });
  },

  loadTree: async () => {
    const { currentSiteId, sortBy, sortOrder } = get();
    set({ treeLoading: true, error: null });
    try {
      const root = sortItems(await sharePointService.listItems(''), sortBy, sortOrder);
      treeChildren.set('', root);
      // Fetch children for each persisted-expanded folder, bounded; drop any
      // that no longer exist so a stale set cannot wedge the tree.
      const expanded = [...get().expandedPaths];
      const results = await mapWithConcurrency(expanded, 4, async (p) => ({
        p,
        items: await sharePointService.listItems(p),
      }));
      const stillValid = new Set<string>();
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          treeChildren.set(r.value.p, sortItems(r.value.items, get().sortBy, get().sortOrder));
          stillValid.add(expanded[i]);
        }
      });
      if (get().currentSiteId === currentSiteId) {
        const pruned = new Set([...get().expandedPaths].filter((p) => stillValid.has(p)));
        persistExpanded(get().browserView, currentSiteId, pruned);
        set({ expandedPaths: pruned, treeLoading: false });
        get().rebuildVisibleRows();
      }
    } catch (error) {
      logger.error('loadTree error:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load folders',
        treeLoading: false,
      });
    }
  },

  reloadFolder: async (path: string) => {
    const { sortBy, sortOrder } = get();
    try {
      const freshItems = sortItems(await sharePointService.listItems(path), sortBy, sortOrder);
      treeChildren.set(path, freshItems);
      get().rebuildVisibleRows();
    } catch (error) {
      logger.debug('reloadFolder failed:', path, error);
    }
  },

  collapseAll: () => {
    const empty = new Set<string>();
    persistExpanded(get().browserView, get().currentSiteId, empty);
    set({ expandedPaths: empty });
    get().rebuildVisibleRows();
  },

  toggleExpand: async (path: string) => {
    const expanded = new Set(get().expandedPaths);
    if (expanded.has(path)) {
      expanded.delete(path);
      persistExpanded(get().browserView, get().currentSiteId, expanded);
      const parent = parentFolderPath(path);
      set({ expandedPaths: expanded, currentPath: parent });
      persistPath(parent);
      get().rebuildVisibleRows();
      return;
    }
    expanded.add(path);
    persistExpanded(get().browserView, get().currentSiteId, expanded);
    set({ expandedPaths: expanded, currentPath: path });
    persistPath(path);
    get().rebuildVisibleRows();
    if (!treeChildren.has(path)) {
      // Show a spinner on the expanding folder while its children load.
      const loading = new Set(get().loadingPaths);
      loading.add(path);
      set({ loadingPaths: loading });
      try {
        await get().reloadFolder(path);
      } finally {
        const done = new Set(get().loadingPaths);
        done.delete(path);
        set({ loadingPaths: done });
      }
    }
    // During search, manually expanding a folder reveals its real children
    // (otherwise the filtered tree would prune them).
    if (get().isSearching) {
      for (const c of treeChildren.get(path) || []) searchReveal.add(c.path);
      get().rebuildVisibleRows();
    }
  },

  // Auth actions
  initAuth: async () => {
    logger.debug('initAuth starting');
    // Store creation happens before Office.onReady. Re-read here so Outlook
    // uses roamingSettings once the host context is ready; standalone reloads
    // the equivalent localStorage schema.
    get().hydrateFolderFavorites();
    try {
      await authService.initialize();
      // NAA: silently establish the signed-in Office user on load - no sign-in button, no popup.
      let account = authService.getAccount();
      if (!account) {
        account = await authService.login();
      }
      logger.debug('initAuth complete, account:', account?.username || 'none');

      set({
        auth: {
          isAuthenticated: !!account,
          isLoading: false,
          user: account ? { name: account.name || '', email: account.username } : undefined,
        },
      });

      // Once authenticated, load the accessible sites and either restore the
      // last-used site or show the home/site-picker view.
      if (account && authService.isAuthenticated()) {
        logger.debug('User authenticated, loading sites');
        await get().loadSites();
        const persisted = readLocal(SITE_STORAGE_KEY);
        const site = get().sites.find((s) => s.id === persisted && s.accessible);
        if (site) {
          // Restore the last open folder too: the host tears the taskpane down
          // on reply/discard/item switches, so reloads must land the user back
          // where they were, not at the site root.
          get().selectSite(site.id, getPersistedPath());
        }
        // On mobile, capture the open email so the SaveBar can offer "Save here".
        get().initSaveMode();
      } else {
        logger.debug('User not authenticated, showing login screen');
      }
    } catch (error) {
      logger.error('Auth init error:', error);
      set({
        auth: {
          isAuthenticated: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Auth initialization failed',
        },
      });
    }
  },

  login: async () => {
    set({ auth: { ...get().auth, isLoading: true } });
    try {
      const account = await authService.login();
      if (account) {
        set({
          auth: {
            isAuthenticated: true,
            isLoading: false,
            user: { name: account.name || '', email: account.username },
          },
        });
        get().refresh();
      }
    } catch (error) {
      logger.error('Login error:', error);
      set({
        auth: {
          isAuthenticated: false,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Login failed',
        },
      });
    }
  },

  logout: async () => {
    await authService.logout();
    sharePointService.setSiteId(null);
    set({
      auth: { isAuthenticated: false, isLoading: false },
      items: [],
      currentPath: '',
      currentSiteId: null,
    });
  },

  // Multi-site
  loadSites: async (forceTokenRefresh = false) => {
    // The manual home-view Refresh forces a fresh Graph token first, so the
    // access probes reflect current membership (sites gained or lost) instead of
    // a stale cached token. Best-effort: a refresh failure still falls through to
    // probing with whatever token is cached.
    if (forceTokenRefresh) {
      await authService.getGraphToken(undefined, true).catch(() => undefined);
    }
    // One batched probe. Keep the last-known access on a transient blip: a
    // failed probe (deploy cache window, network) or an uncertain per-site
    // sub-status must NOT flip a working site to "no access". Only a definite
    // accessible/denied result updates it.
    const sites = config.sharePoint.sites;
    const prev = new Map(get().sites.map((s) => [s.id, s.accessible]));
    try {
      const access = await sharePointService.sitesAccessible(sites.map((s) => s.id));
      const probed = sites.map((s) => {
        const a = access[s.id];
        return { ...s, accessible: a === undefined ? (prev.get(s.id) ?? false) : a };
      });
      set({ sites: probed, sitesLoaded: true, authStale: false });
      // Telemetry: the "no access" screen state, and recovery from a REAL one.
      // accessRecovered only fires after an actual noAccess was surfaced (see
      // sawNoAccess), so a normal boot from the default all-inaccessible state
      // does not masquerade as a recovery.
      const anyNow = probed.some((s) => s.accessible);
      if (!anyNow) {
        sawNoAccess = true;
        trackEvent('sites.noAccess', { forceTokenRefresh });
      } else if (sawNoAccess) {
        sawNoAccess = false;
        trackEvent('sites.accessRecovered');
      }
    } catch (error) {
      logger.warn('Site access probe failed; keeping last-known access:', error);
      const status = (error as { status?: number })?.status;
      set({ sitesLoaded: true, authStale: status === 401 });
    }
  },

  selectSite: (id: string, initialPath: string = '') => {
    // initialPath is only passed by the boot restore; picking a site from the
    // home view always starts at the site root.
    sharePointService.setSiteId(id);
    writeLocal(SITE_STORAGE_KEY, id);
    treeChildren.clear();
    delete viewStash.all;
    delete viewStash.favorites;
    set({
      currentSiteId: id,
      currentPath: initialPath,
      items: [],
      visibleRows: [],
      selectedItems: new Set(),
      error: null,
      expandedPaths: readExpanded('all', id),
      browserView: 'all',
      favoriteAvailability: {},
    });
    persistPath(initialPath);
    void get()
      .loadTree()
      .then(() => {
        // If the restored folder no longer exists (deleted or renamed since the
        // last session), fall back to the site root instead of an error view.
        const now = get();
        if (initialPath && now.error && now.currentPath === initialPath) {
          get().navigate('');
        }
      });
  },

  goHome: () => {
    sharePointService.setSiteId(null);
    writeLocal(SITE_STORAGE_KEY, '');
    set({
      currentSiteId: null,
      currentPath: '',
      items: [],
      selectedItems: new Set(),
      error: null,
      browserView: 'all',
    });
    persistPath('');
  },

  // Navigation
  navigate: (path: string) => {
    // Navigating leaves search mode (e.g. opening a search-result folder).
    if (searchTimer) clearTimeout(searchTimer);
    set({
      currentPath: path,
      selectedItems: new Set(),
      error: null,
      searchQuery: '',
      isSearching: false,
    });
    persistPath(path); // Save to localStorage
    get().refresh();
  },

  navigateUp: () => {
    const { currentPath } = get();
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length ? parts.join('/') + '/' : '';
    set({ currentPath: newPath, selectedItems: new Set(), error: null });
    persistPath(newPath); // Save to localStorage
    get().refresh();
  },

  refresh: async () => {
    if (get().currentSiteId && !get().isSearching) {
      await get().loadTree();
      return;
    }

    const { currentPath, currentSiteId, sortBy, sortOrder } = get();
    const key = listingKey(currentSiteId, currentPath);
    const cached = listingCache.get(key);

    // refresh always loads a folder listing, so it is never "search" state.
    // With a cache hit the folder renders instantly (no spinner) and the
    // network fetch below revalidates in the background.
    set({ isLoading: !cached, error: null, isSearching: false });
    if (cached) {
      set({ items: sortItems(cached, sortBy, sortOrder) });
    }

    try {
      const items = await sharePointService.listItems(currentPath);
      listingCache.set(key, items);
      // Apply only if the user is still looking at this folder.
      const now = get();
      if (
        now.currentPath === currentPath &&
        now.currentSiteId === currentSiteId &&
        !now.isSearching
      ) {
        set({ items: sortItems(items, now.sortBy, now.sortOrder), isLoading: false });
      }
    } catch (error) {
      logger.error('Refresh error:', error);
      if (cached) {
        // The user already sees the cached listing; do not replace it with an
        // error screen for a failed background revalidation.
        set({ isLoading: false });
      } else {
        set({
          error: error instanceof Error ? error.message : 'Failed to load items',
          isLoading: false,
        });
      }
    }
  },

  silentRefresh: async () => {
    const { currentSiteId, isSearching, currentPath, sortBy, sortOrder, items, isUploading } =
      get();
    // Only when browsing a site (not the home picker, not mid-search, not mid-upload).
    if (!currentSiteId || isSearching || isUploading) return;
    try {
      const raw = await sharePointService.listItems(currentPath);
      listingCache.set(listingKey(currentSiteId, currentPath), raw);
      const fresh = sortItems(raw, sortBy, sortOrder);
      // Update only when the folder actually changed, to avoid flicker / re-render.
      if (listSignature(fresh) !== listSignature(items)) {
        logger.debug('Auto-refresh: folder changed, updating list');
        set({ items: fresh });
      }
    } catch (error) {
      // Background refresh failures are non-fatal and must not disrupt the UI.
      logger.debug('Silent refresh failed:', error);
    }
  },

  // Selection
  setSelection: (paths: string[]) => {
    set({ selectedItems: new Set(paths) });
  },

  selectAll: () => {
    set({
      selectedItems: new Set(
        get()
          .getDisplayedItems()
          .map((i) => i.path),
      ),
    });
  },

  clearSelection: () => {
    set({ selectedItems: new Set() });
  },

  // View
  setSortBy: (sortBy) => {
    const { items, sortOrder } = get();
    resortTreeChildren(sortBy, sortOrder);
    set({ sortBy, items: sortItems(items, sortBy, sortOrder) });
    get().rebuildVisibleRows();
  },

  toggleSortOrder: () => {
    const { items, sortBy, sortOrder } = get();
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    resortTreeChildren(sortBy, newOrder);
    set({ sortOrder: newOrder, items: sortItems(items, sortBy, newOrder) });
    get().rebuildVisibleRows();
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    if (searchTimer) clearTimeout(searchTimer);

    // Empty query -> leave search, restore the user's expansion and the full tree.
    if (!query.trim()) {
      searchReveal = new Set();
      const restore = preSearchExpanded ?? get().expandedPaths;
      preSearchExpanded = null;
      set({ isSearching: false, searchLocalCount: 0, expandedPaths: restore });
      get().rebuildVisibleRows();
      return;
    }

    // Entering search: snapshot the current expansion to restore on clear.
    if (!get().isSearching) {
      preSearchExpanded = new Set(get().expandedPaths);
    }

    // Instant: filter the tree by name across everything already loaded, so the
    // tree collapses to matches immediately (no index wait).
    applySearchReveal(set, get, loadedTreeNameMatches(query.trim().toLowerCase()));

    // Deeper: the server name search finds matches in folders not yet loaded.
    searchTimer = setTimeout(() => {
      get().runSearch(query);
    }, 350);
  },

  runSearch: async (query) => {
    const ql = query.trim().toLowerCase();
    try {
      // Site-wide search, kept to NAME matches (drop content-only hits, which
      // were noisy). Then load the ancestor folders of any match not yet in the
      // tree so the filtered tree can render the path down to it.
      const results = await sharePointService.searchItems(query, '');
      if (get().searchQuery !== query) return;
      const serverMatches = results.filter((r) => r.name.toLowerCase().includes(ql));

      const needed = new Set<string>();
      for (const m of serverMatches) {
        for (const a of ancestorsOf(m.path)) {
          if (!treeChildren.has(a)) needed.add(a);
        }
      }
      if (needed.size > 0) {
        const loaded = await mapWithConcurrency([...needed], 4, async (p) => ({
          p,
          items: await sharePointService.listItems(p),
        }));
        const { sortBy, sortOrder } = get();
        loaded.forEach((r) => {
          if (r.status === 'fulfilled') {
            treeChildren.set(r.value.p, sortItems(r.value.items, sortBy, sortOrder));
          }
        });
      }
      if (get().searchQuery !== query) return;

      // Recompute matches over the now-fuller tree (server matches are present
      // in their parents' loaded children) and reveal them.
      applySearchReveal(set, get, loadedTreeNameMatches(ql));
    } catch (error) {
      logger.error('Search error:', error);
      // Keep whatever the instant client-side filter already revealed.
    }
  },

  setBrowserView: (view) => {
    if (view === 'favorites' && get().isSearching) {
      get().setSearchQuery('');
    }
    const current = get().browserView;
    if (view !== current) {
      stashCurrentView(get);
      const snapshot = takeViewSnapshot(view, get().currentSiteId);
      resortTreeChildren(snapshot.sortBy, snapshot.sortOrder);
      set({
        browserView: view,
        selectedItems: new Set(),
        expandedPaths: snapshot.expandedPaths,
        sortBy: snapshot.sortBy,
        sortOrder: snapshot.sortOrder,
      });
    } else {
      set({ selectedItems: new Set() });
    }
    get().rebuildVisibleRows();
    if (view === 'favorites') {
      void hydrateFavoriteRoots(get);
    }
  },

  hydrateFolderFavorites: () => set({ folderFavorites: loadFolderFavorites() }),

  getSiteFavorites: () => {
    const siteId = get().currentSiteId;
    return siteId ? get().folderFavorites.filter((favorite) => favorite.siteId === siteId) : [];
  },

  isFolderFavorite: (path) => {
    const siteId = get().currentSiteId;
    return (
      !!siteId &&
      get().folderFavorites.some((favorite) => favorite.siteId === siteId && favorite.path === path)
    );
  },

  addFolderFavorite: async (item) => {
    const siteId = get().currentSiteId;
    if (!siteId) return;
    const key = favoriteKey(siteId, item.path);
    if (
      get().folderFavorites.some((favorite) => favoriteKey(favorite.siteId, favorite.path) === key)
    ) {
      return;
    }
    const next = [
      ...get().folderFavorites,
      { siteId, path: item.path, name: item.name, isFolder: item.isFolder },
    ];
    set((state) => {
      const availability = { ...state.favoriteAvailability };
      delete availability[key];
      return { folderFavorites: next, favoriteAvailability: availability };
    });
    if (get().browserView === 'favorites') {
      get().rebuildVisibleRows();
      void hydrateFavoriteRoots(get);
    }
    try {
      const saved = await queueFavoritesWrite(next);
      if (get().folderFavorites === next) set({ folderFavorites: saved });
      trackEvent('favorites.added', { siteId });
    } catch (error) {
      logger.warn('Unable to persist folder favorite:', error);
      get().notify('error', 'The favorite was added for this session but could not be saved.');
    }
  },

  removeFolderFavorite: async (path) => {
    const siteId = get().currentSiteId;
    if (!siteId) return;
    const key = favoriteKey(siteId, path);
    const current = get().folderFavorites;
    const next = current.filter((favorite) => favorite.siteId !== siteId || favorite.path !== path);
    if (next.length === current.length) return;
    sessionOnlyFavoriteHydrationCache.delete(key);
    set((state) => {
      const availability = { ...state.favoriteAvailability };
      delete availability[key];
      return { folderFavorites: next, favoriteAvailability: availability };
    });
    if (get().browserView === 'favorites') get().rebuildVisibleRows();
    try {
      const saved = await queueFavoritesWrite(next);
      if (get().folderFavorites === next) set({ folderFavorites: saved });
      trackEvent('favorites.removed', { siteId });
    } catch (error) {
      logger.warn('Unable to persist removed folder favorite:', error);
      get().notify(
        'error',
        'The favorite was removed for this session but the change could not be saved.',
      );
    }
  },

  openFolderFavorite: async (favorite) => {
    const siteId = get().currentSiteId;
    if (!siteId || favorite.siteId !== siteId) return false;
    const key = favoriteKey(siteId, favorite.path);
    try {
      const chain = [...ancestorsOf(favorite.path).reverse(), favorite.path];
      const loaded = new Map<string, BlobItem[]>();
      for (const folderPath of chain) {
        const parent = parentFolderPath(folderPath);
        let children = loaded.get(parent);
        if (!children) {
          const listed = await sharePointService.listItems(parent);
          if (get().currentSiteId !== siteId) return false;
          children = sortItems(listed, get().sortBy, get().sortOrder);
          loaded.set(parent, children);
          treeChildren.set(parent, children);
        }
        if (!children.some((item) => item.isFolder && item.path === folderPath)) {
          throw Object.assign(new Error('Favorite folder not found'), { status: 404 });
        }
      }

      if (get().currentSiteId !== siteId) return false;
      const wasFavorites = get().browserView === 'favorites';
      if (wasFavorites) stashCurrentView(get);
      const allSnapshot = wasFavorites
        ? takeViewSnapshot('all', siteId)
        : {
            expandedPaths: new Set(get().expandedPaths),
            sortBy: get().sortBy,
            sortOrder: get().sortOrder,
          };
      const expanded = allSnapshot.expandedPaths;
      for (const ancestor of ancestorsOf(favorite.path)) expanded.add(ancestor);
      if (wasFavorites) resortTreeChildren(allSnapshot.sortBy, allSnapshot.sortOrder);
      persistExpanded('all', siteId, expanded);
      persistPath(favorite.path);
      set((state) => ({
        browserView: 'all',
        currentPath: favorite.path,
        expandedPaths: expanded,
        sortBy: allSnapshot.sortBy,
        sortOrder: allSnapshot.sortOrder,
        searchQuery: '',
        isSearching: false,
        selectedItems: new Set(),
        error: null,
        favoriteAvailability: { ...state.favoriteAvailability, [key]: 'available' },
      }));
      get().rebuildVisibleRows();
      trackEvent('favorites.opened', { siteId });
      return true;
    } catch (error) {
      if (get().currentSiteId !== siteId) return false;
      const status = (error as { status?: number })?.status;
      const outcome = status === 404 ? 'not_found' : status === 403 ? 'forbidden' : 'transient';
      if (status === 403 || status === 404) {
        set((state) => ({
          favoriteAvailability: { ...state.favoriteAvailability, [key]: 'unavailable' },
        }));
      } else {
        get().notify('error', 'The favorite could not be opened. Please try again.');
      }
      trackEvent('favorites.unavailable', { siteId, outcome });
      return false;
    }
  },

  setError: (error) => set({ error }),

  notify: (type, text) => {
    set({ notification: { type, text } });
    if (type === 'success') {
      setTimeout(() => {
        const current = get().notification;
        if (current && current.type === 'success' && current.text === text) {
          set({ notification: null });
        }
      }, 2500);
    }
  },

  dismissNotification: () => set({ notification: null }),

  // File operations
  createFolder: async (name: string) => {
    const { currentPath } = get();
    set({ isCreatingFolder: true });

    try {
      await sharePointService.createFolder(currentPath, name);
      listingCache.delete(listingKey(get().currentSiteId, currentPath));
      set({ isCreateFolderOpen: false, isCreatingFolder: false });

      // Log folder creation
      get().addActivity('create_folder', `Created folder "${name}"`, undefined, currentPath || '/');

      await get().reloadFolder(currentPath);
    } catch (error) {
      set({ isCreatingFolder: false });
      get().addActivity(
        'error',
        `Failed to create folder "${name}"`,
        error instanceof Error ? error.message : 'Unknown error',
        currentPath || '/',
      );
      throw error;
    }
  },

  deleteItem: async (item: BlobItem) => {
    logger.debug('deleteItem called for:', item.name, item.path);
    set({ isLoading: true });
    try {
      if (item.isFolder) {
        await sharePointService.deleteFolder(item.path);
      } else {
        await sharePointService.deleteBlob(item.path);
      }
      // Clear selection and refresh
      const { selectedItems, currentPath } = get();
      listingCache.delete(listingKey(get().currentSiteId, currentPath));
      const newSelection = new Set(selectedItems);
      newSelection.delete(item.path);
      set({ selectedItems: newSelection, isLoading: false });

      // Log deletion
      get().addActivity(
        'delete',
        `Deleted ${item.isFolder ? 'folder' : 'file'} "${item.name}"`,
        undefined,
        currentPath || '/',
      );

      await get().reloadFolder(parentFolderPath(item.path));
    } catch (error) {
      logger.error('Delete error:', error);
      set({ isLoading: false });
      get().notify('error', error instanceof Error ? error.message : 'Delete failed');
      get().addActivity(
        'error',
        `Failed to delete "${item.name}"`,
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  },

  deleteSelected: async () => {
    const { selectedItems } = get();
    logger.debug('deleteSelected called, items:', Array.from(selectedItems));

    if (selectedItems.size === 0) {
      logger.debug('No items selected');
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const selectedPaths = Array.from(selectedItems);
      const selectedItemObjects = get()
        .getDisplayedItems()
        .filter((item) => selectedPaths.includes(item.path));

      logger.debug(
        'Deleting items:',
        selectedItemObjects.map((i) => i.name),
      );

      // Bounded concurrency so a large selection cannot flood Graph; each
      // delete settles independently so one failure does not mask the rest.
      const results = await mapWithConcurrency(selectedItemObjects, 4, (item) =>
        item.isFolder
          ? sharePointService.deleteFolder(item.path)
          : sharePointService.deleteBlob(item.path),
      );
      const failed = selectedItemObjects.filter((_, i) => results[i].status === 'rejected');
      if (failed.length > 0) {
        const first = results.find((r) => r.status === 'rejected') as
          | PromiseRejectedResult
          | undefined;
        logger.error(
          'Some deletes failed:',
          failed.map((f) => f.name),
          first?.reason,
        );
        get().notify(
          'error',
          `Failed to delete ${failed.length} of ${selectedItemObjects.length} item(s): ${failed
            .map((f) => f.name)
            .slice(0, 3)
            .join(', ')}${failed.length > 3 ? ` and ${failed.length - 3} more` : ''}`,
        );
      }

      listingCache.delete(listingKey(get().currentSiteId, get().currentPath));
      set({ selectedItems: new Set(), isLoading: false });
      // Reload each distinct parent folder of the deleted items.
      const parentPaths = new Set(selectedItemObjects.map((i) => parentFolderPath(i.path)));
      await Promise.all([...parentPaths].map((p) => get().reloadFolder(p)));
    } catch (error) {
      logger.error('Delete selected error:', error);
      get().notify('error', error instanceof Error ? error.message : 'Delete failed');
      set({ isLoading: false });
    }
  },

  downloadSelected: async () => {
    const { selectedItems } = get();
    const files = get()
      .getDisplayedItems()
      .filter((i) => selectedItems.has(i.path) && !i.isFolder);
    if (files.length === 0) return;
    // Pool of 2: enough overlap to be quick without the browser suppressing
    // the burst of programmatic download clicks.
    const results = await mapWithConcurrency(files, 2, async (item) => {
      await sharePointService.downloadBlob(item);
    });
    const failed = results.filter((r) => r.status === 'rejected').length;
    get().addActivity(
      'download',
      `Downloaded ${files.length - failed} of ${files.length} selected file(s)`,
    );
    if (failed > 0) {
      get().notify('error', `Failed to download ${failed} of ${files.length} file(s).`);
    }
  },

  downloadItem: async (item: BlobItem) => {
    if (item.isFolder) return;
    try {
      await sharePointService.downloadBlob(item);
      get().addActivity('download', `Downloaded "${item.name}"`);
      get().notify('success', `Downloaded "${item.name}"`);
    } catch (err) {
      // Log technical errors but show generic message to user
      logger.warn('Download error:', err);
      get().notify('error', `Unable to download "${item.name}". Please try again.`);
    }
  },

  openFile: async (item: BlobItem) => {
    // Everything opens at its canonical SharePoint URL in a new tab: files in
    // the proper Office/web viewer, folders in the library view. Graph always
    // supplies webUrl; if an item somehow lacks one there is nothing to open.
    if (item.webUrl) {
      window.open(item.webUrl, '_blank');
    } else {
      logger.warn('Item has no SharePoint URL to open:', item.path);
    }
  },

  copyLink: async (item: BlobItem) => {
    // Copies the item's SharePoint URL. This grants no new access - only people
    // who already have access to the site (assigned members) can open it; the
    // tenant's internal-only sharing policy blocks external use.
    if (!item.webUrl) {
      get().notify('error', 'No shareable link is available for this item.');
      return;
    }
    const copied = await copyTextToClipboard(item.webUrl);
    if (copied) {
      get().notify('success', `Link copied for "${item.name}"`);
    } else {
      // Last resort: surface the URL so the user can copy it manually.
      logger.error('Copy link failed; clipboard unavailable for', item.webUrl);
      get().notify('error', `Copy this link manually: ${item.webUrl}`);
    }
  },

  attachSelectedToEmail: async () => {
    const { selectedItems } = get();

    if (selectedItems.size === 0) {
      set({ error: 'No files selected to attach.' });
      return;
    }

    // Get the selected file items (not folders)
    const filesToAttach = get()
      .getDisplayedItems()
      .filter((item) => selectedItems.has(item.path) && !item.isFolder);

    if (filesToAttach.length === 0) {
      set({ error: 'No files selected (folders cannot be attached).' });
      return;
    }

    await attachFilesToEmail(set, get, filesToAttach, true);
  },

  attachItemToEmail: async (item: BlobItem) => {
    if (item.isFolder) {
      get().notify('error', 'Folders cannot be attached.');
      return;
    }
    await attachFilesToEmail(set, get, [item], false);
  },

  // Upload
  uploadFiles: async (files: File[], targetPath?: string) => {
    const { currentPath, uploads } = get();
    const destination = targetPath ?? currentPath;

    let uploadIdCounter = Date.now();
    const newUploads: UploadProgress[] = files.map((file) => ({
      id: `upload-${uploadIdCounter++}`,
      fileName: file.name,
      progress: 0,
      status: 'pending',
      targetPath: destination || '/',
    }));

    set({ uploads: [...uploads, ...newUploads], isUploading: true });
    // Show the chevron-style spinner on the destination folder so a drop has
    // immediate, localized feedback while the items upload (cleared after the
    // folder is reloaded below).
    if (destination) {
      const loading = new Set(get().loadingPaths);
      loading.add(destination);
      set({ loadingPaths: loading });
    }

    // Bounded pool (#33): hash, dedup-check, and upload run concurrently per
    // file instead of one serial await per file, so a multi-email drop takes
    // roughly max(latency) x ceil(N/pool) rather than N x latency. Progress
    // and failures stay per-file (tray entries are keyed by upload id).
    // Concurrency 3 (was 4): gentler on SharePoint write throttling for a
    // big multi-email drop; fetchGraph also retries any 429 with backoff.
    await mapWithConcurrency(files, 3, async (file, i) => {
      const uploadId = newUploads[i].id;

      // ContentHash is stamped on every upload as the dedup doc_id consumed
      // by the migration tagging pass and the future AI Search index
      // (write-time duplicate DETECTION was removed - SharePoint's filter
      // throttling on large libraries made it unreliable; dedup is a
      // migration/index-time concern). Whole-file reads fail above the
      // browser's ~2 GB ArrayBuffer limit, so very large files skip hashing.
      let contentHash: string | undefined;
      let fileData: ArrayBuffer | undefined;
      if (file.size <= config.features.maxHashSize) {
        try {
          fileData = await file.arrayBuffer();
          contentHash = await sharePointService.generateContentHash(fileData);
        } catch (error) {
          logger.warn(
            'File metadata read skipped:',
            error instanceof Error ? error.name : 'unknown',
          );
        }
      }

      const previewKind = getPreviewKind(file.name);
      const emailKind = previewKind === 'eml' || previewKind === 'msg' ? previewKind : undefined;
      let hasAttachments: boolean | undefined;
      if (fileData && emailKind) {
        try {
          hasAttachments = await emailService.detectEmailAttachments(fileData, emailKind);
        } catch (error) {
          logger.warn(
            'Attachment metadata unavailable; saving without HasAttachments:',
            error instanceof Error ? error.name : 'unknown parser error',
          );
        }
      }

      if (!fileData && file.name.toLowerCase().endsWith('.eml')) {
        try {
          fileData = await file.slice(0, EMAIL_HEADER_READ_LIMIT).arrayBuffer();
        } catch (error) {
          logger.debug(`Received date skipped for "${file.name}":`, error);
        }
      }

      let emailReceivedDate: Date | undefined;
      if (fileData && isEmailFile(file.name)) {
        try {
          emailReceivedDate = await emailService.getReceivedDateFromFile(file.name, fileData);
        } catch (error) {
          logger.debug(`Received date skipped for "${file.name}":`, error);
        }
      }

      await performUpload(
        set,
        get,
        uploadId,
        file,
        destination,
        contentHash,
        emailReceivedDate,
        hasAttachments,
      );
    });

    listingCache.delete(listingKey(get().currentSiteId, destination));
    set({ isUploading: false });
    await get().reloadFolder(destination);
    // Clear the destination folder spinner now that its contents are refreshed.
    if (destination) {
      const done = new Set(get().loadingPaths);
      done.delete(destination);
      set({ loadingPaths: done });
    }
  },

  cancelUpload: (uploadId: string) => {
    uploadAborters.get(uploadId)?.abort();
  },

  cancelAllUploads: () => {
    for (const aborter of uploadAborters.values()) {
      aborter.abort();
    }
  },

  clearCompletedUploads: () => {
    set({ uploads: [] });
  },

  // Preview
  setPreviewItem: (item) => {
    set({ previewItem: item, previewMetadata: null, previewContent: null, previewHtml: null });
    if (item) {
      get().loadPreview(item);
    }
  },

  loadPreview: async (item: BlobItem) => {
    if (item.isFolder) return;

    // Only emails and text-like files are fetched here; the preview panel
    // renders Office/PDF/media itself (server-side preview URL or blob).
    const kind = getPreviewKind(item.name);
    if (kind !== 'eml' && kind !== 'msg' && kind !== 'text') return;
    if ((item.size || 0) > config.features.maxPreviewSize) return;

    set({ isPreviewLoading: true });

    try {
      if (kind === 'eml' || kind === 'msg') {
        // Proper MIME/MSG parsing: real HTML body (sandboxed iframe in the
        // panel) with inline images resolved, or the plain-text part.
        const blob = await sharePointService.getFileBlob(item.path);
        const parsed = await emailService.parseEmailPreview(await blob.arrayBuffer(), kind);
        set({
          previewMetadata: parsed.metadata,
          previewContent: parsed.text || null,
          previewHtml: parsed.html || null,
          isPreviewLoading: false,
        });
      } else {
        const content = await sharePointService.getFileContent(item.path);
        set({
          previewContent: content.slice(0, 5000),
          isPreviewLoading: false,
        });
      }
    } catch (error) {
      // Only log to console
      logger.warn('Preview load warning:', error);
      set({
        isPreviewLoading: false,
      });
    }
  },

  // Drag
  setIsDragging: (isDragging) => set({ isDragging }),
  setDragOverPath: (path) => set({ dragOverPath: path }),

  handleDrop: async (_targetPath: string) => {
    set({ isDragging: false, dragOverPath: null });
  },

  // Mobile email save mode -----------------------------------------------
  // Detect the host once; on mobile, capture the open read message so the
  // SaveBar can offer "Save here" as the user browses. No-op on desktop/web.
  initSaveMode: () => {
    if (!detectMobile()) {
      set({ isMobile: false, saveEmail: null });
      return;
    }
    set({ isMobile: true, saveEmail: emailService.getReadItemRef() });
  },

  // Save the captured email into the folder the user has navigated to.
  saveCurrentEmailHere: async () => {
    const { saveEmail, currentPath } = get();
    if (!saveEmail) return;
    set({ isSavingEmail: true });
    try {
      const [result] = await saveMessages(
        [
          {
            kind: 'itemId',
            itemId: saveEmail.itemId,
            subject: saveEmail.subject,
            date: saveEmail.date,
          },
        ],
        currentPath,
      );
      const where = currentPath
        ? currentPath.replace(/\/+$/, '').split('/').pop() || 'this folder'
        : 'the site root';
      if (result.status === 'saved') {
        get().addActivity(
          'upload',
          `Saved email "${saveEmail.subject}"`,
          undefined,
          currentPath || '/',
        );
        get().notify('success', `Saved to ${where}`);
        set({ saveEmail: null });
        await get().reloadFolder(currentPath);
      } else if (result.status === 'duplicate') {
        get().notify('error', 'This email is already in this folder.');
      } else {
        get().notify('error', result.error || 'Could not save the email.');
      }
    } catch (err) {
      logger.error('Save email error:', err);
      get().notify('error', err instanceof Error ? err.message : 'Could not save the email.');
    } finally {
      set({ isSavingEmail: false });
    }
  },

  // Leave save mode without saving; the pane reverts to a plain browser.
  cancelSaveMode: () => set({ saveEmail: null }),

  // Dialogs
  setCreateFolderOpen: (open) => set({ isCreateFolderOpen: open }),
  isDeleteConfirmOpen: false,
  setDeleteConfirmOpen: (open) => set({ isDeleteConfirmOpen: open }),
  moveDialogItems: null,
  openMoveDialog: (items) => set({ moveDialogItems: items }),
  closeMoveDialog: () => set({ moveDialogItems: null }),

  renameDialogItem: null,
  openRenameDialog: (item) => set({ renameDialogItem: item }),
  closeRenameDialog: () => set({ renameDialogItem: null }),

  renameItem: async (item, newName) => {
    const trimmed = newName.trim();
    // No-op on an empty or unchanged name.
    if (!trimmed || trimmed === item.name) {
      set({ renameDialogItem: null });
      return;
    }
    const parent = parentFolderPath(item.path);
    set({ isLoading: true, error: null, renameDialogItem: null });
    try {
      await sharePointService.renameItem(item.path, trimmed);
      // A folder rename re-paths its whole subtree on the server; drop its stale
      // cached children and expansion so the tree reloads cleanly under the new
      // name (the old path no longer exists).
      if (item.isFolder) {
        treeChildren.delete(item.path);
        const expanded = new Set(get().expandedPaths);
        expanded.delete(item.path);
        purgeStashedExpandedPath(item.path);
        set({ expandedPaths: expanded });
      }
      listingCache.delete(listingKey(get().currentSiteId, parent));
      get().addActivity(
        'rename',
        `Renamed "${item.name}" to "${trimmed}"`,
        undefined,
        parent || '/',
      );
      get().notify('success', `Renamed to "${trimmed}"`);
      set({ selectedItems: new Set(), isLoading: false });
      await get().reloadFolder(parent);
    } catch (err) {
      set({ isLoading: false });
      logger.error('Rename error:', err);
      get().notify('error', err instanceof Error ? err.message : 'Rename failed');
      get().addActivity(
        'error',
        `Failed to rename "${item.name}"`,
        err instanceof Error ? err.message : 'Unknown error',
      );
    }
  },

  moveItems: async (items: BlobItem[], destFolder: string) => {
    const { currentPath, currentSiteId } = get();
    const destClean = destFolder.replace(/^\/+/, '').replace(/\/+$/, '');

    // Skip no-op moves (already in the destination) and guard against moving
    // a folder into itself or its own subtree.
    const movable = items.filter((item) => {
      const parent = item.path.replace(/\/+$/, '').split('/').slice(0, -1).join('/');
      if (parent === destClean) return false;
      if (item.isFolder) {
        const folderClean = item.path.replace(/\/+$/, '');
        if (destClean === folderClean || destClean.startsWith(`${folderClean}/`)) return false;
      }
      return true;
    });
    if (movable.length === 0) {
      set({ moveDialogItems: null });
      return;
    }

    set({ isLoading: true, error: null, moveDialogItems: null });
    const results = await mapWithConcurrency(movable, 3, (item) =>
      sharePointService.moveItem(item.path, destClean),
    );
    const failed = movable.filter((_, i) => results[i].status === 'rejected');
    const moved = movable.length - failed.length;

    if (failed.length > 0) {
      const first = results.find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined;
      logger.error(
        'Some moves failed:',
        failed.map((f) => f.name),
        first?.reason,
      );
      get().notify(
        'error',
        `Failed to move ${failed.length} of ${movable.length} item(s): ${failed
          .map((f) => f.name)
          .slice(0, 3)
          .join(', ')}${failed.length > 3 ? ` and ${failed.length - 3} more` : ''}`,
      );
    }
    if (moved > 0) {
      get().addActivity(
        'upload',
        `Moved ${moved} item(s) to "${destClean || 'root'}"`,
        undefined,
        destClean || '/',
      );
    }

    const destFolderPath = destClean ? `${destClean}/` : '';
    const foldersToRefresh = new Set<string>([
      currentPath,
      destFolderPath,
      ...movable.map((item) => parentFolderPath(item.path)),
    ]);
    foldersToRefresh.forEach((p) => listingCache.delete(listingKey(currentSiteId, p)));
    listingCache.delete(listingKey(currentSiteId, destClean));
    set({ selectedItems: new Set(), isLoading: false });
    await Promise.all([...foldersToRefresh].map((p) => get().reloadFolder(p)));
  },

  // Activity log
  addActivity: (type, message, details, path) => {
    const entry: ActivityLogEntry = {
      id: `activity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      message,
      details,
      path,
      timestamp: new Date(),
    };
    set((state) => ({
      activityLog: [entry, ...state.activityLog].slice(0, 100), // Keep last 100 entries
    }));
  },

  clearActivityLog: () => set({ activityLog: [] }),
}));
